import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Progress,
  Modal,
  Typography,
  Tooltip,
  message
} from 'antd';
import { 
  CheckSquareOutlined, 
  DeleteOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const TaskManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  // 获取任务列表
  const { data: tasksData, isLoading } = useQuery(
    ['tasks', searchText, statusFilter, priorityFilter, assigneeFilter, projectFilter],
    () => axios.get('/tasks', {
      params: {
        search: searchText || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        assignedTo: assigneeFilter || undefined,
        projectId: projectFilter || undefined,
        limit: 100
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 获取用户列表
  const { data: usersData } = useQuery(
    'users',
    () => axios.get('/auth/users').then(res => res.data.data)
  );

  // 获取项目列表
  const { data: projectsData } = useQuery(
    'projects',
    () => axios.get('/projects', { params: { limit: 1000 } }).then(res => res.data.data)
  );

  // 删除任务
  const deleteTaskMutation = useMutation(
    (taskId) => axios.delete(`/tasks/${taskId}`),
    {
      onSuccess: () => {
        message.success('任务删除成功');
        queryClient.invalidateQueries('tasks');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '删除失败');
      }
    }
  );

  const handleDeleteTask = (task) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除任务"${task.title}"吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => deleteTaskMutation.mutate(task.id)
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority] || 'default';
  };

  const getPriorityText = (priority) => {
    const texts = {
      high: '高',
      medium: '中',
      low: '低'
    };
    return texts[priority] || priority;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待开始',
      in_progress: '进行中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return texts[status] || status;
  };

  const isOverdue = (dueDate, status) => {
    return status !== 'completed' && dueDate && dayjs().isAfter(dayjs(dueDate));
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {isOverdue(record.dueDate, record.status) && (
            <Tag color="red" style={{ marginLeft: '8px' }}>逾期</Tag>
          )}
        </div>
      )
    },
    {
      title: '所属项目',
      dataIndex: 'project',
      key: 'project',
      render: (project) => (
        <Button 
          type="link" 
          size="small"
          onClick={() => navigate(`/projects/${project.id}`)}
        >
          {project.name}
        </Button>
      )
    },
    {
      title: '执行人',
      dataIndex: 'assignee',
      key: 'assignee',
      render: (assignee) => assignee?.realName || '未指定'
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress) => (
        <Progress 
          percent={progress} 
          size="small"
          status={progress === 100 ? 'success' : 'active'}
        />
      )
    },
    {
      title: '工时',
      key: 'hours',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: '12px' }}>
            预估: {record.estimatedHours || '-'}h
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            实际: {record.actualHours || 0}h
          </Text>
        </Space>
      )
    },
    {
      title: '截止时间',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => date ? (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(date).format('MM-DD HH:mm')}
        </Tooltip>
      ) : '-'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/projects/${record.project.id}`)}
          />
          {['admin', 'manager', 'pm', 'designer'].includes(user?.role) && (
            <Button 
              type="text" 
              danger
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteTask(record)}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <CheckSquareOutlined />
            任务管理
          </Space>
        }
        className="status-card"
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: '16px' }}>
          <Space wrap>
            <Search
              placeholder="搜索任务名称或描述"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="所属项目"
              allowClear
              style={{ width: 200 }}
              onChange={setProjectFilter}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {projectsData?.projects?.map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="任务状态"
              allowClear
              style={{ width: 120 }}
              onChange={setStatusFilter}
            >
              <Option value="pending">待开始</Option>
              <Option value="in_progress">进行中</Option>
              <Option value="completed">已完成</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
            <Select
              placeholder="优先级"
              allowClear
              style={{ width: 120 }}
              onChange={setPriorityFilter}
            >
              <Option value="high">高优先级</Option>
              <Option value="medium">中优先级</Option>
              <Option value="low">低优先级</Option>
            </Select>
            <Select
              placeholder="执行人"
              allowClear
              style={{ width: 150 }}
              onChange={setAssigneeFilter}
              showSearch
              optionFilterProp="children"
            >
              {usersData?.users?.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.realName}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        {/* 任务表格 */}
        <Table
          columns={columns}
          dataSource={tasksData?.tasks || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            total: tasksData?.pagination?.total,
            pageSize: tasksData?.pagination?.limit,
            current: tasksData?.pagination?.page,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个任务`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default TaskManagement;
